/* oxlint-disable react/no-danger */
import { hydrateRoot } from 'react-dom/client';

import { Application } from 'shared/containers/App';
import { IApplicationContext } from 'shared/types/applicationContext';
import { TReduxStore } from 'shared/types/redux';
import { ETelemetryEvents } from 'shared/types/telemetry';

interface IDependencies {
  store: TReduxStore;
  context: IApplicationContext;
}

export function renderApplication(dependencies: IDependencies): void {
  const { store, context } = dependencies;
  const { config, logger, telemetry } = context;

  const projectName = config.getStrict<string>('projectName');
  const rootElement = document.getElementById(projectName);

  if (!rootElement) {
    throw new Error(`Unable to find element #${projectName}`);
  }

  /**
   * Снимок серверной разметки до гидрации: после hydrateRoot DOM уже принадлежит React,
   * а при ошибке гидрации React 19 очищает корень и рендерит его заново на клиенте.
   */
  const ssrMarkup = rootElement.innerHTML;

  /**
   * Заглушка корневого boundary: возвращает в DOM серверную разметку, иначе ErrorLogComponent
   * нарисует пустой <span> и краулер увидит пустую страницу. Интерактивность теряется,
   * контент и ссылки остаются. display: contents — чтобы обёртка не ломала раскладку корня.
   */
  const ssrFallback = <div style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: ssrMarkup }} />;

  hydrateRoot(rootElement, <Application context={context} reduxStore={store} errorStub={ssrFallback} />, {
    /**
     * Ошибку поймал error boundary: корневой показал снимок SSR, локальный скрыл свой блок.
     * Саму ошибку с componentStack пишет ErrorLogComponent, здесь только метрика —
     * она была у удалённого ErrorBoundary и терять её незачем.
     */
    onCaughtError: () => {
      telemetry.increment(ETelemetryEvents.CompanyPageFailed);
    },
    /**
     * Ошибку не поймал ни один boundary — React размонтировал корень. Возвращаем серверную
     * разметку руками, иначе страница останется пустой.
     */
    onUncaughtError: (error, errorInfo) => {
      telemetry.increment(ETelemetryEvents.CompanyPageFailed);
      logger.error(error as Error, {
        domain: 'react.uncaughtError',
        componentStack: errorInfo.componentStack,
      });

      rootElement.innerHTML = ssrMarkup;
    },
  });
}
